import { useSyncExternalStore } from 'react';
import { GameConnection, type ConnectionState } from '@il-colpo/protocol';
import { getAccountStore } from '../account/accountStore.ts';
import { useSettingsStore } from '../state/settingsStore.ts';
import { DEBUG_ENABLED } from '../game/gameStore.ts';
import { gameServerUrl } from './config.ts';

/**
 * The one connection of the page to the game server, made when it is first wanted. It asks the
 * account service for the player's token each time it has to join (a session is renewed in the
 * background, so the token of a minute ago is not the token of now).
 */

let connection: GameConnection | null = null;

export function getConnection(): GameConnection {
  connection ??= new GameConnection({
    endpoint: gameServerUrl() ?? 'ws://localhost:2567',
    token: async () => {
      const client = await getAccountStore().getState().ensure();
      return client ? client.accessToken() : null;
    },
    language: () => useSettingsStore.getState().settings.language,
    appVersion: () => __APP_VERSION__,
  });
  // The handle the end-to-end tests hold on the connection (development builds only): it reads the
  // state and cuts the line the way a network failure would.
  if (DEBUG_ENABLED) (window as unknown as { __colpoOnline?: unknown }).__colpoOnline = connection;
  return connection;
}

/** Whether online play is possible at all in this build: an account service and a server to go to. */
export function onlineAvailable(): boolean {
  return gameServerUrl() !== null && getAccountStore().getState().setup.available;
}

/** What the server has said, re-rendered on every change. */
export function useConnection(): ConnectionState {
  const c = getConnection();
  useSyncExternalStore(
    (listener) => c.subscribe(listener),
    () => c.version(),
  );
  return c.getState();
}
